import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';

import { MarketingInfoForm } from '../ui/marketing-info-form';
import { withErrorHandling } from '@selfserve/react-hocs';
import { useEmailValidation } from '../data';
import {
  StyledContentContainer,
  SplitScreenLayout,
  StyledSideScreen,
} from '@selfserve/react-ui';

import { QuestionData, AnswerData, usePackageDetailRequest } from '../data';
import { useNavigate, useParams } from 'react-router-dom';
import { useContext, useEffect, useState } from 'react';
import { FormikProps, useFormik } from 'formik';
import { apiStatuses, routesPaths } from '@selfserve/react-utils';
import { Redirector } from '../utils/redirector';
import { storage, storageKeys, useStorage } from '@selfserve/react-hooks';
import MarketingInfoContext from '../utils/markerting-info-context';

const StyledMarketingInfoContainer = styled.div`
  max-width: 560px;
  width: 100%;
`;

export interface FormData {
  [key: string]: Array<AnswerData>;
}

const formikToRequest = function (values: FormData) {
  const questions = Object.keys(values);
  const answers = Object.values(values).map((answer) => {
    return answer.filter((ans) => ans.selected);
  });

  const formattedAnswers = questions.map((question, index) => {
    return {
      question: question,
      answers: answers[index].map((answer) => answer.text),
    };
  });

  return formattedAnswers;
};

export const MarketingInfo = withErrorHandling(() => {
  const { t } = useTranslation();
  const [savedPackageData] = useStorage(storage.SESSION, storageKeys.PACKAGE_DATA);
  const [savedSubscriptionId] = useStorage(storage.SESSION, storageKeys.SUBSCRIPTION_ID);
  const [getQestions, setQuestions] = useState([] as Array<QuestionData>);
  const [getCurrentPage, setCurrentPage] = useState({} as QuestionData);
  const [getCurrentPageIndex, setCurrentPageIndex] = useState(0);
  const [isPreparedToSend, setIsPreparedToSend] = useState(false);
  const { questionName, subscriptionId } = useParams();
  const [getId, setId] = useState<string>();
  const packageData = savedPackageData || usePackageDetailRequest(subscriptionId as string);
  const emailValidationData = savedPackageData?  undefined : useEmailValidation(subscriptionId as string);
  const navigate = useNavigate();
  const [savedData, setSavedData] = useStorage(
    storage.LOCAL,
    storageKeys.MARKETING_INFO + subscriptionId
  );

  const {setTokenContext } = useContext(MarketingInfoContext);
  const [, setAccessToken] = useStorage(
    storage.SESSION,
    storageKeys.TOKEN
  );

  useEffect(() => {
    if (emailValidationData?.data?.data?.access_token !== undefined) {
      setTokenContext(emailValidationData?.data?.data?.access_token);
      setAccessToken(emailValidationData?.data?.data?.access_token);
    }
  }, [emailValidationData?.data?.data?.access_token]);

  const onSubmit = () => {
    if (getCurrentPageIndex + 1 === getQestions.length) {
      setIsPreparedToSend(true);
      return;
    }

    navigate(
      '../' +
        routesPaths.MARKETING_INFO.replace(
          ':subscriptionId',
          subscriptionId as string
        ).replace(
          ':questionName',
          getQestions[getCurrentPageIndex + 1].name as string
        )
    );
  };

  if (!savedData) {
    setSavedData({});
  }

  const formik: FormikProps<FormData> = useFormik<FormData>({
    initialValues: savedData,
    onSubmit,
  });

  const values = formik.values;
  useEffect(() => {
    setSavedData(values);
  }, [values]);

  useEffect(() => {
    if(savedPackageData)
    setQuestions(savedPackageData.marketing_info);
  }, [savedPackageData]);

  useEffect(() => {
    if(savedSubscriptionId)
      setId(savedSubscriptionId);
  }, [savedSubscriptionId]);
  
  useEffect(() => {
    if (
      packageData.status === 'success' &&
      packageData.data.status === apiStatuses.SUCCESS
    )
      setQuestions(
        packageData.data?.data?.marketing_info || ([] as Array<QuestionData>)
      );
  }, [packageData, setQuestions, setId]);

  useEffect(() => {
    if(!(savedPackageData)) {
      if (emailValidationData?.data?.data?.subscriptionId)
        setId(emailValidationData?.data.data.subscriptionId);

      if (
        (emailValidationData?.data?.status === apiStatuses.NOT_FOUND ||
          emailValidationData?.data?.data?.status === 'Link already used') &&
        !getId
      )
        navigate('../' + routesPaths.LINK_USED);
    }
  }, [emailValidationData, setId]);

  useEffect(() => {
    const newPage = getQestions.filter(
      (question) => question.name === questionName
    )[0];
    const newPageIndex = getQestions.indexOf(newPage as QuestionData);

    setCurrentPageIndex(newPageIndex);
    setCurrentPage(newPage || ({} as QuestionData));
  }, [questionName, setCurrentPageIndex, getQestions]);

  return (
    <>
      <Helmet>
        <title>{t('titles.marketing-info')}</title>
      </Helmet>

      {getId && isPreparedToSend && (
        <Redirector
          subscriptionId={getId}
          data={formikToRequest(formik.values)}
        />
      )}
      <SplitScreenLayout
        secondScreen={
          <StyledSideScreen
            imageUrl={
              (getCurrentPage.imageUrl ||
                process.env[`NX_Q${getCurrentPageIndex + 1}_IMAGE`]) as string
            }
          />
        }
      >
        <StyledContentContainer centered>
          <StyledMarketingInfoContainer>
            <MarketingInfoForm
              isLoading={isPreparedToSend}
              question={getCurrentPage}
              formik={formik}
              questionsAmount={getQestions.length}
              currentPageIndex={getCurrentPageIndex}
            />
          </StyledMarketingInfoContainer>
        </StyledContentContainer>
      </SplitScreenLayout>
    </>
  );
});
